import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import {
  AddShoppingCart as AddShoppingCartIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { trackAddToCart } from '../../utils/googleTag';

interface SimpleAddToCartButtonProps {
  market: {
    id: string;
    name: string;
  };
  selectedPrice?: {
    priceId: string;
    amount: number;
    interval: 'month' | 'year';
    productId: string;
  };
  onAddToCart?: (item: any) => void;
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  hidePrice?: boolean;
  disabled?: boolean;
  isInCart?: boolean;
}

// Helper function to format price
const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};

export const SimpleAddToCartButton: React.FC<SimpleAddToCartButtonProps> = ({
  market,
  selectedPrice,
  onAddToCart,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  hidePrice = false,
  disabled = false,
  isInCart = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddToCart = async () => {
    if (!selectedPrice) {
      setError('Please select a pricing option');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Simulate a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Call the parent's add to cart handler if provided
      if (onAddToCart) {
        const interval = selectedPrice.interval === 'month' ? 'monthly' : 'yearly';
        
        onAddToCart({
          marketId: market.id,
          marketName: market.name,
          priceId: selectedPrice.priceId,
          price: selectedPrice.amount,
          productId: selectedPrice.productId,
          interval: interval
        });
      }
      
      // Track add to cart event for Google Ads
      trackAddToCart(
        selectedPrice.priceId,
        market.name,
        selectedPrice.amount,
        'USD'
      );
      
      setAdded(true);
      
      // Reset the "added" state after 2 seconds
      setTimeout(() => {
        setAdded(false);
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError('Failed to add item to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (isInCart) {
      return 'Already in Cart';
    }
    if (added) {
      return 'Added to Cart';
    }
    if (hidePrice || !selectedPrice) {
      return 'Add to Cart';
    }
    return `Add to Cart - ${formatPrice(selectedPrice.amount)}`;
  };

  const getButtonIcon = () => {
    if (isInCart || added) {
      return <CheckIcon />;
    }
    return <AddShoppingCartIcon />;
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        onClick={handleAddToCart}
        disabled={loading || disabled || added || isInCart}
        startIcon={loading ? <CircularProgress size={20} /> : getButtonIcon()}
        color={added || isInCart ? 'success' : 'primary'}
        sx={{
          transition: 'all 0.3s ease',
          ...((added || isInCart) && {
            backgroundColor: 'success.light',
            '&:hover': {
              backgroundColor: 'success.light',
            },
          }),
        }}
      >
        {getButtonText()}
      </Button>
      
      {/* Inline error alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 1 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};